import type { MessageInstance } from 'antd/es/message/interface';
import { btlApplicationFormApi } from 'api/btlApplicationForm';
import type { BtlApplication } from 'api/btlApplicationForm/types';
import { CreateCaseDto, RefinancePurpose } from 'api/btlApplicationForm/types';
import { getPageURLWithSection, PageURL } from 'router/pageURL';
import { config } from 'utils/config';
import { buildUpdateFinanceAndPropertyDto } from './buildUpdateFinanceAndPropertyDto';
import { mapApplicationToFormValues } from './mapApplicationToFormValues';

interface SubmitFinancingAndPropertyFormParams {
  values: Record<string, any>;
  data?: BtlApplication;
  id: string;
  propertyId?: string;
  nextSection: string;
  navigate: (path: string) => void;
  setIsSubmitting: (isSubmitting: boolean) => void;
  message: MessageInstance;
}

function normalizeFeesAddedToBalance(value: unknown, fallback: boolean): boolean {
  if (Array.isArray(value)) {
    return value[0] ?? false;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  return fallback;
}

function normalizeTermInMonths(value: unknown, fallbackMonths: number): number {
  if (typeof value !== 'number') {
    return fallbackMonths;
  }

  if (value <= 40) {
    return value * 12;
  }

  return value;
}

export async function submitFinancingAndPropertyForm({
  values,
  data,
  id,
  propertyId,
  nextSection,
  navigate,
  setIsSubmitting,
  message,
}: SubmitFinancingAndPropertyFormParams) {
  const { env } = config;
  const baseValues = mapApplicationToFormValues(data);
  const mergedValues: Record<string, any> = { ...baseValues, ...values };

  const {
    purpose,
    financingAmount,
    outstandingBalance,
    purchasePrice,
    grossMonthlyRentalPayment,
    expectedPropertyType,
    paymentProfile,
    repaymentStrategy,
    repaymentStrategyDetails,
    refinancePurpose,
    feesAddedToBalance,
    termInMonths,
    ...rest
  } = mergedValues;

  try {
    const resolvedPurpose = purpose ?? data?.purpose;
    const resolvedRefinancePurpose =
      resolvedPurpose === 'PURCHASE' ? null : refinancePurpose ?? data?.refinancePurpose ?? null;

    setIsSubmitting(true);

    if (id) {
      const updateCaseDto = buildUpdateFinanceAndPropertyDto({
        caseId: id,
        propertyId,
        mergedValues,
        data,
      });

      await btlApplicationFormApi.updateFinanceAndProperty(id, updateCaseDto);
      message.success(`Application updated${env.VITE_ENV === 'production' ? '' : ` with id: ${id}`}`);
      navigate(getPageURLWithSection(PageURL.BTL_APPLICATION_EDIT, { id }, { section: nextSection }));
      return;
    }

    const createCaseDto = {
      purpose: resolvedPurpose,
      financingAmount,
      outstandingBalance:
        resolvedRefinancePurpose === RefinancePurpose.LIKE_FOR_LIKE ? financingAmount : outstandingBalance,
      ...rest,
      refinancePurpose: resolvedRefinancePurpose,
      feesAddedToBalance: normalizeFeesAddedToBalance(feesAddedToBalance, false),
      termInMonths: normalizeTermInMonths(termInMonths, 0),
      paymentProfile,
      repaymentStrategy,
      repaymentStrategyDetails,
      property: {
        purchasePrice,
        grossMonthlyRentalPayment,
        expectedPropertyType,
      },
      submissionType: 'INTRODUCED',
    } as unknown as CreateCaseDto;

    const appId = await btlApplicationFormApi.createApplication(createCaseDto);
    message.success(`Application created${env.VITE_ENV === 'production' ? '' : ` with id: ${appId}`}`);
    navigate(getPageURLWithSection(PageURL.BTL_APPLICATION_EDIT, { id: appId }, { section: nextSection }));
  } catch (error: unknown) {
    console.error(error);
    message.error(error instanceof Error ? error.message : 'Failed to save application');
  } finally {
    setIsSubmitting(false);
  }
}
